/**
 * La session d'un élève avec le champion, vue du masteur (pur) : son historique et son bac « À traiter ».
 *
 * Une suggestion ne se perd jamais (Wajih, 02/11) : sa naissance laisse une trace ; tant que sa condition
 * tient, elle est dans le bac, au-dessus du champ ; périmée ou décidée, elle en sort d'elle-même et laisse
 * une seconde trace qui dit pourquoi. Le bac n'est fait que des suggestions ouvertes : une proposition
 * périmée n'y a plus de bouton.
 *
 * Au retour sur un élève, le repère « Depuis ton dernier passage » se pose avant la première ligne plus
 * récente que le dernier passage — seulement s'il sépare quelque chose.
 */
import { countLabel } from "./textFormatter"
import { type UIActivityGenerationCatalogs, type UIActivityGenerationDraft } from "../types/activityGeneration"
import {
    type UITutorFeedItem,
    type UITutorFeedSignal,
    type UITutorRibbonEventKind,
    type UITutorSessionElsewhere,
    type UITutorSessionEntry,
    type UITutorSessionSetting,
    type UITutorSessionSettingField,
    type UITutorSessionTrace,
    type UITutorSuggestion,
} from "../types/tutorLive"
import { tutorFeedPriorityRank } from "./tutorFeedFormatter"

/**
 * Le rang dans le bac : ce que le champion a déjà fait et qui se défait encore (une relance en direct,
 * annulable) passe devant, puis la priorité de l'ancien panneau.
 */
function trayRank(suggestion: UITutorSuggestion): number {
    return suggestion.item.sam === "acts" ? 0 : tutorFeedPriorityRank(suggestion.item.priority)
}

/** La trace d'une suggestion qui a vécu sans décision : discrète, elle dit laquelle et où elle est passée. */
const expiredText = (item: UITutorFeedItem) => `sans décision : « ${item.text} »`

/** Deux instants ISO : leur ordre, minuit compris. */
function byTime(a: string, b: string): number {
    return Date.parse(a) - Date.parse(b)
}

/**
 * La durée de vie d'une suggestion, en minutes, par signal (Wajih, round 4 : « au bout d'un certain temps,
 * les suggestions ou décisions doivent disparaître et se mettre juste dans l'historique »). Elle s'ajoute à
 * la condition de validité : passé ce délai sans décision, la carte quitte le bac et devient une trace
 * discrète. `null` : elle vit tant que son signal est vrai — une main levée, un élève qui se tait ou qui est
 * sorti, un rendu à valider ne périment pas ; ils s'escaladent comme en prod (« Sans réponse »).
 */
const TUTOR_SUGGESTION_LIFETIME: Record<UITutorFeedSignal, number | null> = {
    hand_raised: null,
    silence: null,
    out_of_app: null,
    exercise_done: null,
    sam_ongoing: null,
    auto_activity: 5,
    sam_summary: 10,
    sam_proposal: 5,
    sam_whisper: 2,
    sam_nudge: 1,
}

/** L'instant (ISO) où une suggestion née à `at` quitte le bac d'elle-même ; absent : jamais. */
export function tutorSuggestionExpiresAt(signal: UITutorFeedSignal, at: string): string | undefined {
    const minutes = TUTOR_SUGGESTION_LIFETIME[signal]
    return minutes == null ? undefined : new Date(Date.parse(at) + minutes * 60_000).toISOString()
}

type Timed = { entry: UITutorSessionEntry; at: string; order: number }

export function tutorSessionTimeline(input: {
    entries: UITutorSessionEntry[]
    suggestions: UITutorSuggestion[]
    /** L'instant (ISO) du dernier passage du masteur sur cet élève. Absent : pas de repère. */
    lastSeenAt?: string
    /** L'instant (ISO) qu'il est : une suggestion ouverte dont la durée de vie est passée devient une trace discrète. */
    now?: string
}): { history: UITutorSessionEntry[]; tray: UITutorFeedItem[] } {
    const { entries, suggestions, lastSeenAt, now } = input
    // L'instant où une suggestion ouverte a quitté le bac ; absent : elle y est encore.
    const expiredAt = (suggestion: UITutorSuggestion) => {
        if (suggestion.state.kind !== "open" || now == null) return undefined
        const end = tutorSuggestionExpiresAt(suggestion.item.signal, suggestion.at)
        return end != null && byTime(end, now) <= 0 ? end : undefined
    }
    const timed: Timed[] = []
    for (const entry of entries) {
        if (entry.kind === "since") continue
        timed.push({ entry, at: entry.at, order: timed.length })
    }
    // Une ligne par suggestion, qui porte son issue : sa fin ne fait plus une seconde ligne (le bruit du fil). Périmée
    // ou rangée sans décision, elle se lit en retrait ; décidée, elle garde son poids. Elle a l'id de sa carte, celui du
    // point que la tuile en montre.
    for (const suggestion of suggestions) {
        const { item, at, createdText, state } = suggestion
        const expired = expiredAt(suggestion)
        const outcome = state.kind !== "open" ? state.text : expired != null ? expiredText(item) : undefined
        timed.push({
            entry: {
                kind: "trace",
                id: item.id,
                at,
                text: createdText,
                ...(outcome != null && { outcome }),
                ...((state.kind === "stale" || expired != null) && { quiet: true }),
            },
            at,
            order: timed.length,
        })
    }
    timed.sort((a, b) => byTime(a.at, b.at) || a.order - b.order)
    const history = timed.map((t) => t.entry)

    if (lastSeenAt != null) {
        const first = timed.findIndex((t) => byTime(t.at, lastSeenAt) > 0)
        if (first > 0) history.splice(first, 0, { kind: "since", id: "since" })
    }

    const tray = suggestions
        .filter((suggestion) => suggestion.state.kind === "open" && expiredAt(suggestion) == null)
        .sort((a, b) => trayRank(a) - trayRank(b) || byTime(a.at, b.at))
        .map((suggestion) => suggestion.item)
    return { history, tray }
}

/**
 * Les réglages d'une génération lus dans le brouillon du formulaire d'aujourd'hui
 * (`UIActivityGenerationDraft`, aucune donnée nouvelle) : « 4ème · Maths · Fonctions affines · Entraînement
 * · 5 min ». Un réglage manquant garde sa place, à remplir (`missing`), avec le nom du champ.
 */
export function activityDraftSettings(
    draft: UIActivityGenerationDraft,
    catalogs: UIActivityGenerationCatalogs
): UITutorSessionSetting[] {
    const subject = catalogs.subjects.find((s) => s.id === draft.subjectId)
    const chapter = catalogs.chapters.find((c) => c.id === draft.chapterId)
    const level = catalogs.levels.find((l) => l.value === draft.level)
    const duration = catalogs.durations.find((d) => d.value === draft.duration)
    const setting = (
        field: UITutorSessionSettingField,
        value: string | undefined,
        empty: string,
        iconUri?: string
    ) => ({
        field,
        label: value ?? empty,
        missing: value == null,
        ...(iconUri != null && { iconUri }),
    })
    return [
        setting("class", draft.className ?? undefined, "Classe"),
        setting("subject", subject?.name, "Matière", subject?.iconUrl),
        setting("chapter", chapter?.name, "Chapitre"),
        setting("level", level?.label, "Type", level?.iconUri),
        setting("duration", duration?.label, "Durée"),
    ]
}

/**
 * Les réglages facultatifs de la génération, après ceux d'`activityDraftSettings` : les compétences (« 2
 * compétences » quand le masteur en a coché), et « Personnaliser » avec les centres d'intérêt de l'élève quand
 * il en a (une bascule). Jamais manquants : rien ne les exige.
 */
export function activityDraftOptions(
    draft: UIActivityGenerationDraft,
    options: { interests?: string | null }
): UITutorSessionSetting[] {
    const skills = draft.skillPartPositions.length
    return [
        {
            field: "skills",
            // Aucune cochée : le générateur choisit (la puce porte son nom au-dessus d'elle, W15).
            label: skills === 0 ? "Au choix" : countLabel(skills, "compétence"),
            missing: false,
        },
        ...(options.interests != null && options.interests.trim() !== ""
            ? [
                  {
                      field: "personalize" as const,
                      label: draft.personalizeWithInterests ? "Ses centres d'intérêt" : "Non",
                      missing: false,
                      on: draft.personalizeWithInterests,
                  },
              ]
            : []),
    ]
}

/** Ce qui attend le masteur chez un élève, dit pareil partout (vignette, bulle, « Ailleurs », cloche). */
export function tutorPendingLabel(count: number): string {
    return `${count} à décider`
}

/**
 * Les élèves qui attendent le masteur : ce qu'ils ont à décider, ou une urgence ; les urgences d'abord, puis
 * l'ordre de la pellicule. La colonne en retire l'élève regardé (« Ailleurs ») ; repliée, elle les montre tous.
 */
export function tutorSessionElsewhere(input: {
    students: { id: string; firstname: string; avatarUrl?: string; pending?: number }[]
    items: UITutorFeedItem[]
}): UITutorSessionElsewhere[] {
    const urgentByStudent = new Map<string, UITutorFeedItem>()
    const itemsByStudent = new Map<string, UITutorFeedItem[]>()
    for (const item of input.items) {
        if (item.priority === "urgent" && !urgentByStudent.has(item.student.id))
            urgentByStudent.set(item.student.id, item)
        // Ce qui demande une décision, au même poids que chez l'élève regardé : une carte qui informe reste chez lui.
        if (item.action == null || item.action.passive === true) continue
        const mine = itemsByStudent.get(item.student.id)
        if (mine != null) mine.push(item)
        else itemsByStudent.set(item.student.id, [item])
    }
    const rows: UITutorSessionElsewhere[] = []
    for (const student of input.students) {
        const urgent = urgentByStudent.get(student.id)
        const pending = student.pending ?? 0
        if (pending === 0 && urgent == null) continue
        rows.push({
            student: { id: student.id, firstname: student.firstname, avatarUrl: student.avatarUrl },
            pending,
            urgent,
            items: itemsByStudent.get(student.id) ?? [],
        })
    }
    return rows.sort((a, b) => Number(b.urgent != null) - Number(a.urgent != null))
}

/**
 * Le point du fil d'une ligne du moment, celui que la tuile (`tutorThreadExtras`) et l'onglet « Fil » montrent : une
 * ligne du champion, un quiz, une demande ajoutée ou une proposition envoyée (« donné »), une génération ratée. `null` :
 * pas de point à elle.
 */
export function tutorThreadPoint(
    entry: UITutorSessionEntry
): { kind: Extract<UITutorRibbonEventKind, "helper" | "quiz" | "error" | "sent">; label: string } | null {
    switch (entry.kind) {
        case "helper":
            return { kind: "helper", label: entry.title ?? entry.text }
        case "quiz":
            return { kind: "quiz", label: entry.text }
        case "result":
            return entry.error != null ? { kind: "error", label: entry.error } : null
        case "ready":
            return entry.decided === true ? { kind: "sent", label: entry.title } : null
        case "proposal":
            return entry.state === "sent" ? { kind: "sent", label: entry.title } : null
        default:
            return null
    }
}

/** Ce que dit une trace en une ligne, son issue au bout (« Noé a levé la main · Activité ajoutée »). */
export function tutorTraceText(trace: UITutorSessionTrace): string {
    return trace.outcome != null ? `${trace.text} · ${trace.outcome}` : trace.text
}
